<template>
    <div class="row">
        <div class="position-relative w-100">
            <div class="pt-2 d-flex justify-content-end">
                <a
                    class="text-primary font-weight-bold text-uppercase"
                    style="cursor: pointer;"
                    @click="showExport = !showExport"
                >
                    <i class="fa fa-share-square-o mr-2"></i>
                    Export
                </a>
            </div>
            <transition-group name="fade" mode="in-out">
                <div v-if="showExport" class="row" :key="'goals-export-excel'">
                    <div class="col-12 col-lg-6">
                        <goals-export-excel :patient="patient"></goals-export-excel>
                    </div>
                </div>
                <div class="row pt-2" :key="'goals-status-filter'">
                    <div class="col-12">
                        <goals-status-filter
                            :goal-status-filter="goalStatusFilter"
                            @set-parent-prop="propSetFromChild($event)"
                        ></goals-status-filter>
                    </div>
                </div>
                <div class="row w-100 pt-2" :key="'goals-average-score'">
                    <div
                        v-for="(g, i) in goalsAverageScore.filter(g =>
                            g.title.includes('Behavior')
                        )"
                        class="col-3 px-0 px-sm-2 mb-3"
                    >
                        <goals-average-score
                            :id="`score-ov-b-${i}-${g.title.split(' ')[0]}`"
                            :title="g.title"
                            :progress="g.progress"
                            :color="g.color"
                        ></goals-average-score>
                    </div>
                    <div
                        v-for="(g, i) in goalsAverageScore.filter(
                            g => !g.title.includes('Behavior')
                        )"
                        class="col-3 px-0 px-sm-2 mb-3"
                    >
                        <goals-average-score
                            :id="`score-ov-${i}-${g.title.split(' ')[0]}`"
                            :title="g.title"
                            :progress="g.progress"
                            :color="g.color"
                        ></goals-average-score>
                    </div>
                </div>
                <div v-if="allGoals.length" class="row pt-4" :key="'goals-domain-filter'">
                    <div class="col-12">
                        <goals-domain-filter
                            :goal-domain-filter="goalDomainFilter"
                            :therapists="[]"
                            :domains="domainsWithGoals"
                            @refresh-goals="refreshGoals()"
                            @set-parent-prop="propSetFromChild($event)"
                        ></goals-domain-filter>
                    </div>
                </div>
                <div class="row pt-4" :key="'goals-new'">
                    <div class="col-12">
                        <goals-new
                            :tracking="tracking"
                            :patient="patient"
                            :organization-id="organizationId"
                            :user-id="userId"
                            :domains="domains"
                            :goal-status-filter="goalStatusFilter"
                            @refresh-goals="refreshGoals()"
                            :trackers="customizations"
                        ></goals-new>
                    </div>
                </div>
                <div class="row pb-4" :key="'goals-summary'">
                    <div class="col-12">
                        <goals-summary
                            @set-parent-prop="propSetFromChild($event)"
                            @refresh-goals="refreshGoals()"
                            :dusk="`goal-${g.id}`"
                            :domains="domains"
                            :user-id="userId"
                            v-for="g in goals.filter(goal => goal.domain.name === 'Behavior')"
                            :goal="g"
                            :all-goals="allGoals"
                            :goal-status-filter="goalStatusFilter"
                            :key="g.id"
                        ></goals-summary>
                        <goals-summary
                            @set-parent-prop="propSetFromChild($event)"
                            @refresh-goals="refreshGoals()"
                            :dusk="`goal-${g.id}`"
                            :domains="domains"
                            :user-id="userId"
                            v-for="g in goals.filter(goal => goal.domain.name !== 'Behavior')"
                            :goal="g"
                            :all-goals="allGoals"
                            :goal-status-filter="goalStatusFilter"
                            :key="g.id"
                        ></goals-summary>
                    </div>
                </div>
                <div class="row pb-4" :key="'info'">
                    <div class="alert alert-info w-100 mx-3">
                        Goal mastery is calculated based off of an average of the last three
                        sessions.
                    </div>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<script>
import GoalsExportExcel from "./GoalsExportExcel";
import GoalsAverageScore from "./GoalsAverageScore";
import GoalsSummary from "./GoalsSummary";
import GoalsNew from "./GoalsNew";
import GoalsStatusFilter from "./GoalsStatusFilter";
import GoalsDomainFilter from "./GoalsDomainFilter";

export default {
    name: "Overview",
    props: [
        "goals",
        "goalsAverageScore",
        "goal",
        "allGoals",
        "patient",
        "domains",
        "organizationId",
        "userId",
        "customizations",
    ],
    components: {
        GoalsExportExcel,
        GoalsAverageScore,
        GoalsSummary,
        GoalsNew,
        GoalsStatusFilter,
        GoalsDomainFilter,
    },
    data() {
        return {
            collapsed: false,
            tracking: 0,
            showExport: false,
            goalStatusFilter: 0,
            goalDomainFilter: 0,
        };
    },
    computed: {
        domainsWithGoals() {
            let domainsWithGoals = [];
            this.allGoals.forEach(goal => {
                domainsWithGoals.push(goal.domain_id);
            });
            domainsWithGoals = Array.from(new Set(domainsWithGoals)); // get unique values
            return this.domains.filter(domain => domainsWithGoals.includes(domain.id));
        },
    },
    methods: {
        propSetFromChild(event) {
            const prop = event[0];
            const val = event[1];
            if (this.$props.hasOwnProperty(prop)) {
                this.setParentProp(prop, val);
            } else {
                this.$set(this, prop, val);
            }
            this.applyFilters(prop, val);
        },

        applyDomainFilter(filters) {
            if (this.goalDomainFilter > 0) {
                filters.push({
                    key: "domain_id",
                    value: this.goalDomainFilter,
                    operator: "eq",
                });
            }
            return filters;
        },

        applyStatusFilters(filters) {
            let key,
                val = "";
            switch (this.goalStatusFilter) {
                case 0:
                    key = "is_archived";
                    val = "0";
                    break;
                case 1:
                    key = "is_archived";
                    val = "0";
                    break;
                case 2:
                    key = "archive_reason";
                    val = "Mastered";
                    break;
                case 3: // My archived goals, but not mastered
                    key = "is_archived";
                    val = "1";
                    filters.push({
                        key: "archive_reason",
                        value: "Mastered",
                        operator: "eq",
                        not: true,
                    });
                    break;
            }

            filters.push({
                key: key,
                value: val,
                operator: "eq",
            });

            return filters;
        },

        applyFilters() {
            let filters = [];
            filters = this.applyDomainFilter(filters);
            filters = this.applyStatusFilters(filters);
            this.refreshGoals(filters);
        },
        refreshGoals(val) {
            this.$emit("refresh-goals", val);
        },
    },
};
</script>

<style scoped lang="scss"></style>
